import { isEqual } from "@ms/uno-utilities/lib/local/ObjectUtilities";
import { pxToNumber } from "@ms/uno-utilities/lib/local/StyleUtilities";
// Actions
import { ActionType } from "@ms/planner-actions/lib/local/ActionType";
// Constants
import { CopilotLoadState, CopilotSidecarPanelWidth } from "@ms/uno-constants/lib/local/CopilotConstants";
import { LeftNavMaxWidth, LeftNavMinWidth } from "@ms/uno-constants/lib/local/EvoLeftNavConstants";
import { TaskGroupingOption } from "@ms/uno-constants/lib/local/GroupingConstants";
import { TableName } from "@ms/uno-constants/lib/local/StoreConstants";
import { LeftNavCollapseThreshold } from "@ms/uno-constants/lib/local/UIConstants";
// Models
import { DeltaType } from "@ms/planner-models/lib/local/client/deltaSync/Enumerations";
import { EntityFilter } from "@ms/planner-models/lib/local/client/filter/Filter";
import { TaskAccessContext, TaskEditorTab } from "@ms/planner-models/lib/local/client/planner/Enumerations";
import { DynamicFilterField } from "@ms/planner-models/lib/local/constants/FilterConstants";
import { TaskEditorRenderMode } from "@ms/planner-models/lib/local/types/TaskEditorRenderMode";
// Tables
import { Table } from "@ms/uno-entitycore/lib/local/table/Table";
// Stores
import { EventPublishingStore } from "@ms/uno-fluxcore/lib/local/stores/EventPublishingStore";
import { Store } from "../Store";
// Utilities
import { getCustomFieldDefinitionId, isCustomFieldColumn } from "@ms/planner-models/lib/local/utilities/CustomFieldDefinitionUtilities";
import { ViewName } from "@ms/uno-constants/lib/local/ViewConstants";
import { LocalIdGenerator } from "@ms/uno-utilities/lib/local/LocalIdGenerator";
import { getDefaultGroupingOption, getViewIdsInSameFilterAndGroupingScope, toViewIdString } from "@ms/uno-utilities/lib/local/ViewUtilities";
import { logUnreachableAction } from "../../utilities/LoggingUtilities";
export class ViewStore extends EventPublishingStore {
    get name() {
        return Store.View;
    }
    getViewContext(accessContext, contextEntityId) {
        let viewContextId = this.indexByAccessContext[accessContext]?.[contextEntityId];
        // Fill the view context if it hasn't yet been set for entity id for this given task access context
        if (viewContextId == null) {
            viewContextId = this.setViewContext(accessContext, contextEntityId, false);
        }
        return this.viewContexts.get(viewContextId) ?? {
            isLabelCollapsed: false
        };
    }
    getCurrentTaskGroupingState(viewId, userSpecified = false) {
        const grouping = this.viewConfigTable.get(toViewIdString(viewId))?.taskGrouping;
        return grouping ?? (userSpecified ? TaskGroupingOption.None : getDefaultGroupingOption(viewId));
    }
    getCurrentTaskFilter(viewId) {
        return this.viewConfigTable.get(toViewIdString(viewId))?.taskFilter ?? null;
    }
    getCurrentPlanFilter() {
        return this.currentPlanFilter;
    }
    getSortCriteria(viewId) {
        return this.viewConfigTable.get(toViewIdString(viewId))?.sortCriteria ?? null;
    }
    getGridColumnWidths(viewId) {
        return this.viewConfigTable.get(toViewIdString(viewId))?.gridColumnWidths ?? null;
    }
    getTaskEditorRenderMode() {
        return this.taskEditorRenderMode;
    }
    getGanttZoomScale(viewId) {
        return this.viewConfigTable.get(toViewIdString(viewId))?.ganttZoomScale ?? null;
    }
    getTaskEditorSelectedTab() {
        return this.taskEditorSelectedTab;
    }
    hasUnseenAgentOutput(taskId) {
        return this.unseenAgentOutputTaskIds.has(taskId);
    }
    getLeftNavExpanded() {
        return this.leftNavExpandedState;
    }
    getPrimaryViewWidth() {
        // every width has to include the width of the left nav in uno which is 290px and 38px when collapsed.
        let width = this.leftNavExpandedState ? this.viewWidth - pxToNumber(LeftNavMaxWidth) : this.viewWidth - pxToNumber(LeftNavMinWidth);
        const sidecarState = this.getCopilotSidecarState();
        if (sidecarState.open && !sidecarState.collapsed) {
            width = width - CopilotSidecarPanelWidth;
        }
        return width;
    }
    getFABState() {
        return this.FABState;
    }
    getCopilotLoadState() {
        return this.copilotLoadState;
    }
    getCopilotSidecarState() {
        return this.copilotSidecarState;
    }
    /**
     * Gets the column order for a grid view from roaming data.
     *
     * @param viewId The view identifier containing name and partition key
     * @returns Array of column IDs in display order, or null if no custom order is saved
     *
     * @example
     * ```typescript
     * const columnOrder = viewStore.getGridColumnOrder(viewId);
     * // Returns: ["priority", "title", "assignedTo", "dueDate"] or null
     * ```
     */ getGridColumnOrder(viewId) {
        // TODO: Bug 2612581: Add handling plan related actions to ViewStore once evo plan's gridSetting is available
        // Check view settings from roaming data
        const viewIdString = toViewIdString(viewId);
        return this.viewSettingsRoamingData[viewIdString]?.gridColumns ?? null;
    }
    /**
     * Gets the hidden columns for a grid view from roaming data.
     *
     * @param viewId The view identifier containing name and partition key
     * @returns Array of hidden column IDs, or empty array if no hidden columns are saved
     */ getHiddenGridColumns(viewId) {
        const viewIdString = toViewIdString(viewId);
        return this.viewSettingsRoamingData[viewIdString]?.hiddenColumns ?? [];
    }
    getDynamicFilterDescription(viewId) {
        const currentTaskFilter = this.getCurrentTaskFilter(viewId);
        return currentTaskFilter?.filters?.find((f)=>f.field === DynamicFilterField)?.label ?? null;
    }
    getFilterKeyword(viewId) {
        const currentTaskFilter = this.getCurrentTaskFilter(viewId);
        const currentlyTypedText = currentTaskFilter?.filters?.find((filter)=>{
            return filter.field === "displayName";
        })?.values?.[0] ?? "";
        return currentlyTypedText.toString();
    }
    getAddGoalRowVisible() {
        return this.addGoalRowVisible;
    }
    getPersistedTables() {
        return {
            [TableName.ViewConfig]: this.viewConfigTable
        };
    }
    // TODO: Bug 2612581: Add handling plan related actions to ViewStore once evo plan's gridSetting is available
    handleAction(action) {
        let storeChanged = false;
        switch(action.type){
            case ActionType.SetCategoryLabelModeAction:
                storeChanged = this.handleSetCategoryLabelModeAction(action);
                break;
            case ActionType.SetTaskFilterAction:
                storeChanged = this.handleSetTaskFilterAction(action);
                break;
            case ActionType.SetTaskGroupingStateAction:
                storeChanged = this.handleSetTaskGroupingStateAction(action);
                break;
            case ActionType.SetPlanFilterAction:
                storeChanged = this.handleSetPlanFilterAction(action);
                break;
            case ActionType.DeleteEntitySucceededAction:
                storeChanged = this.removeDeletedBucketFromTaskFilter(action.entityId);
                break;
            case ActionType.DeltaSyncSucceededAction:
                const deletedIds = action.deltas.filter((delta)=>delta.type === DeltaType.Delete).map((delta)=>delta.entity.id);
                for (const deletedId of deletedIds){
                    storeChanged = this.removeDeletedBucketFromTaskFilter(deletedId) || storeChanged;
                }
                break;
            case ActionType.SetSortCriteriaAction:
                storeChanged = this.handleViewConfigUpdate([
                    toViewIdString(action.viewId)
                ], {
                    sortCriteria: action.sortCriteria
                });
                break;
            case ActionType.SetGridColumnWidthAction:
                storeChanged = this.handleSetGridColumnWidthAction(action);
                break;
            case ActionType.SetLeftNavExpandedAction:
                storeChanged = this.setLeftNavExpanded(action);
                break;
            case ActionType.SetFABStateAction:
                storeChanged = this.setFABState(action);
                break;
            case ActionType.SetCopilotPaneStateAction:
                storeChanged = this.setCopilotLoadState(action);
                break;
            case ActionType.SetCopilotSidecarStateAction:
                storeChanged = this.setCopilotSidecarState(action);
                break;
            case ActionType.FetchRoamingUserDataSucceededAction:
                storeChanged = this.handleFetchRoamingUserDataSucceededAction(action);
                break;
            case ActionType.UpdateRoamingUserDataAction:
                storeChanged = this.handleUpdateRoamingUserDataAction(action);
                break;
            case ActionType.UpdateRoamingUserDataSucceededAction:
                storeChanged = this.handleUpdateRoamingUserDataSucceededAction(action);
                break;
            case ActionType.UpdateRoamingUserDataFailedAction:
                storeChanged = this.handleUpdateRoamingUserDataFailedAction();
                break;
            case ActionType.SetScreenWidthAction:
                storeChanged = this.setScreenWidthAction(action);
                break;
            case ActionType.ReportValidCustomFieldDefinitionsAction:
                storeChanged = this.handleReportValidCustomFieldDefinitionsAction(action);
                break;
            case ActionType.SetAddGoalRowVisibilityAction:
                storeChanged = this.setAddGoalRowVisibility(action);
                break;
            case ActionType.SetTaskEditorRenderModeAction:
                storeChanged = this.setTaskEditorRenderMode(action.taskEditorRenderMode);
                break;
            case ActionType.SetGanttZoomScaleAction:
                storeChanged = this.handleViewConfigUpdate([
                    toViewIdString(action.viewId)
                ], {
                    ganttZoomScale: action.ganttZoomScale
                });
                break;
            case ActionType.SetTaskEditorSelectedTabAction:
                storeChanged = this.setTaskEditorSelectedTab(action.selectedTab);
                break;
            case ActionType.SetAgentOutputUnseenAction:
                storeChanged = this.setAgentOutputUnseen(action);
                break;
            default:
                logUnreachableAction(this, this.loggers, action);
                return;
        }
        if (storeChanged) {
            this.publishStoreChangedEvent();
        }
    }
    handleSetTaskFilterAction(action) {
        const { viewId, filter } = action;
        const allViewIdStrings = getViewIdsInSameFilterAndGroupingScope(viewId).map(toViewIdString);
        return this.handleViewConfigUpdate(allViewIdStrings, {
            taskFilter: filter
        });
    }
    handleSetTaskGroupingStateAction(action) {
        const { viewId, groupingState } = action;
        const allViewIdStrings = getViewIdsInSameFilterAndGroupingScope(viewId).map(toViewIdString);
        return this.handleViewConfigUpdate(allViewIdStrings, {
            taskGrouping: groupingState
        });
    }
    /**
     * Handle the report valid custom field definitions action.
     * If the current grouping option for the plan view is not a known TaskGroupingOption and not
     * a valid custom field definition ID, reset the grouping to the default.
     * If the current sort criteria references a custom field column whose definition is no longer
     * valid, clear the sort criteria to null.
     */ handleReportValidCustomFieldDefinitionsAction(action) {
        const { planId, validCustomFieldDefinitions } = action;
        const viewId = {
            name: ViewName.PlanPage,
            partitionKey: planId
        };
        const viewIdStrings = getViewIdsInSameFilterAndGroupingScope(viewId).map(toViewIdString);
        const validCfdIds = new Set(validCustomFieldDefinitions.map((cfd)=>cfd.id));
        const knownGroupingOptions = new Set(Object.values(TaskGroupingOption));
        let storeChanged = false;
        for (const viewIdString of viewIdStrings){
            const viewConfig = this.viewConfigTable.get(viewIdString);
            const configUpdate = {};
            // Validate grouping
            const currentGrouping = viewConfig?.taskGrouping;
            if (currentGrouping != null) {
                if (!knownGroupingOptions.has(currentGrouping) && !validCfdIds.has(currentGrouping)) {
                    configUpdate.taskGrouping = getDefaultGroupingOption(viewId);
                }
            }
            // Validate sort criteria
            const sortField = viewConfig?.sortCriteria?.primary?.field;
            if (sortField != null && isCustomFieldColumn(sortField)) {
                const cfdId = getCustomFieldDefinitionId(sortField);
                if (!validCfdIds.has(cfdId)) {
                    configUpdate.sortCriteria = null;
                }
            }
            if (Object.keys(configUpdate).length > 0) {
                storeChanged = this.handleViewConfigUpdate([
                    viewIdString
                ], configUpdate) || storeChanged;
            }
        }
        return storeChanged;
    }
    handleViewConfigUpdate(viewIdStrings, configUpdate) {
        let storeChanged = false;
        for (const viewIdString of viewIdStrings){
            const currentViewConfig = this.viewConfigTable.get(viewIdString) ?? {
                taskFilter: null,
                taskGrouping: null,
                sortCriteria: null,
                gridColumnWidths: null,
                ganttZoomScale: null
            };
            storeChanged = this.viewConfigTable.set(viewIdString, {
                ...currentViewConfig,
                ...configUpdate
            }) || storeChanged;
        }
        return storeChanged;
    }
    handleSetCategoryLabelModeAction(action) {
        const { accessContext, isCollapsed, contextEntityId } = action;
        const viewContextId = this.indexByAccessContext[accessContext]?.[contextEntityId];
        if (viewContextId == null) {
            this.setViewContext(accessContext, contextEntityId, isCollapsed);
            return true;
        } else if (this.viewContexts.get(viewContextId)?.isLabelCollapsed !== isCollapsed) {
            this.viewContexts.set(viewContextId, {
                isLabelCollapsed: isCollapsed
            });
            return true;
        }
        return false;
    }
    /**
     * Handle the action to set the input state of the keyword filter in My Plans view
     * @param action Action to set the keyword filter
     */ handleSetPlanFilterAction(action) {
        const { filter } = action;
        if (!isEqual(filter, this.currentPlanFilter)) {
            this.currentPlanFilter = filter;
            return true;
        }
        return false;
    }
    /**
     * Remove the bucket id from the task entity filter if present
     * @param bucketId Id of the bucket
     */ removeDeletedBucketFromTaskFilter(bucketId) {
        let storeChanged = false;
        for (const viewIdString of this.viewConfigTable.itemIds()){
            const taskFilters = this.viewConfigTable.get(viewIdString)?.taskFilter;
            if (taskFilters?.doesFilterValueExist("bucketId", bucketId)) {
                storeChanged = this.handleViewConfigUpdate([
                    viewIdString
                ], {
                    taskFilter: taskFilters.toggleFilterValue("bucketId", bucketId)
                });
            }
        }
        return storeChanged;
    }
    /**
     * Handle set grid column width action
     * @param action Action fired when set grid column width
     */ handleSetGridColumnWidthAction(action) {
        const { viewId, columnId, columnWidth } = action;
        const viewIdString = toViewIdString(viewId);
        const currentColumnWidths = this.viewConfigTable.get(viewIdString)?.gridColumnWidths || {};
        const newColumnWidths = {
            ...currentColumnWidths,
            [columnId]: columnWidth
        };
        return this.handleViewConfigUpdate([
            viewIdString
        ], {
            gridColumnWidths: newColumnWidths
        });
    }
    /**
     * Handle fetch roaming user data succeeded action
     * @param action Action fired when fetch roaming user data succeeded
     */ handleFetchRoamingUserDataSucceededAction(action) {
        const { roamingUserData } = action;
        this.viewSettingsRoamingData = roamingUserData.viewSettings;
        return true;
    }
    /**
     * Handle update roaming user data action
     * @param action Action fired when update roaming user data
     */ handleUpdateRoamingUserDataAction(action) {
        const { roamingUserData } = action;
        // Store the previous settings
        this.previousViewSettingsRoamingData = {
            ...this.viewSettingsRoamingData
        };
        // Update view settings optimistically
        this.viewSettingsRoamingData = roamingUserData.viewSettings;
        return true;
    }
    /**
     * Handle update roaming user data succeeded action
     * @param action Action fired when update roaming user data succeeded
     */ handleUpdateRoamingUserDataSucceededAction(action) {
        const { roamingUserData } = action;
        // Clear the previous setting since the update succeeded
        this.previousViewSettingsRoamingData = null;
        // Check whether the view settings need to be updated
        if (!isEqual(this.viewSettingsRoamingData, roamingUserData.viewSettings)) {
            this.viewSettingsRoamingData = roamingUserData.viewSettings;
            return true;
        }
        return false;
    }
    /**
     * Handle update roaming user data failed action
     */ handleUpdateRoamingUserDataFailedAction() {
        // Revert back to the previous settings
        if (this.previousViewSettingsRoamingData) {
            this.viewSettingsRoamingData = {
                ...this.previousViewSettingsRoamingData
            };
            this.previousViewSettingsRoamingData = null;
            return true;
        }
        return false;
    }
    /**
     *
     * @param accessContext Access context to set labels collapsed state for
     * @param contextEntityId Id of the entity in the given access context
     * @param isLabelCollapsed Whether the labels should be set to collapsed
     */ setViewContext(accessContext, contextEntityId, isLabelCollapsed) {
        // Generate a new view context id
        const viewContextId = LocalIdGenerator.generate();
        // Create a new view context
        const viewContext = {
            isLabelCollapsed
        };
        // Add to BasicTable
        this.viewContexts.set(viewContextId, viewContext);
        // Add to index
        this.indexByAccessContext[accessContext][contextEntityId] = viewContextId;
        return viewContextId;
    }
    /**
     * Set the left nav expanded state
     * @param action Action to set the left nav expanded state
     */ setLeftNavExpanded(action) {
        if (this.leftNavExpandedState !== action.isExpanded) {
            this.leftNavExpandedState = action.isExpanded;
            return true;
        }
        return false;
    }
    /**
     * Set the screen width
     * @param action Action to set the screen width size
     */ setScreenWidthAction(action) {
        const width = action.width;
        if (this.viewWidth !== width) {
            this.viewWidth = width;
            return true;
        }
        return false;
    }
    /**
     * Set the FAB mode
     * @param action Action to set the FAB mode
     */ setFABState(action) {
        if (!isEqual(this.FABState, action.state)) {
            this.FABState = action.state;
            return true;
        }
        return false;
    }
    /**
     * Set the Copilot pane load state
     * @param action Action to set the Copilot pane load state
     */ setCopilotLoadState(action) {
        if (this.copilotLoadState === action.state) {
            return false;
        }
        this.copilotLoadState = action.state;
        return true;
    }
    /**
     * Set the Copilot sidecar state
     * @param action Action to set the Copilot sidecar state
     */ setCopilotSidecarState(action) {
        const { open, initialHandInContext } = action.state;
        const collapsed = action.state.collapsed ?? false;
        // initialHandInContext is only meaningful on first open (closed → open transition).
        // Subsequent state changes (e.g., reopen from collapsed) use handIn directly.
        const isFirstOpen = open && !this.copilotSidecarState.open;
        const nextState = {
            open,
            collapsed,
            initialHandInContext: isFirstOpen ? initialHandInContext : undefined
        };
        if (this.copilotSidecarState.open === nextState.open && this.copilotSidecarState.collapsed === nextState.collapsed && isEqual(this.copilotSidecarState.initialHandInContext, nextState.initialHandInContext)) {
            return false;
        }
        this.copilotSidecarState = nextState;
        return true;
    }
    /**
     * Set the add goal row visibility
     * @param action Action to set the add goal row visibility
     */ setAddGoalRowVisibility(action) {
        if (this.addGoalRowVisible !== action.visible) {
            this.addGoalRowVisible = action.visible;
            return true;
        }
        return false;
    }
    /**
     * Set the global task editor render mode
     * @param renderMode The render mode to set
     */ setTaskEditorRenderMode(renderMode) {
        if (this.taskEditorRenderMode !== renderMode) {
            this.taskEditorRenderMode = renderMode;
            return true;
        }
        return false;
    }
    /**
     * Set the task editor selected tab
     * @param selectedTab The tab to set
     */ setTaskEditorSelectedTab(selectedTab) {
        if (this.taskEditorSelectedTab !== selectedTab) {
            this.taskEditorSelectedTab = selectedTab;
            return true;
        }
        return false;
    }
    /**
     * Set the unseen agent output flag for a task.
     * Set to true when agent is assigned/transitions; cleared when user views the Agent tab.
     */ setAgentOutputUnseen(action) {
        if (action.unseen) {
            if (this.unseenAgentOutputTaskIds.has(action.taskId)) {
                return false;
            }
            this.unseenAgentOutputTaskIds.add(action.taskId);
            return true;
        }
        return this.unseenAgentOutputTaskIds.delete(action.taskId);
    }
    /**
     * Initialize a new instance of the ViewStore
     * @param loggers The loggers for telemetry
     * @param persistentStorage The persistent storage to use
     */ constructor(loggers){
        super(), this.loggers = loggers, // General view related configurations
        this.viewContexts = new Table("viewContexts"), this.viewConfigTable = new Table(TableName.ViewConfig, (c1, c2)=>isEqual(c1, c2)), // Filter applies on plan view
        this.currentPlanFilter = new EntityFilter([]), // Project manager button state
        this.FABState = {
            mode: "collapsed"
        }, // Copilot pane load state
        this.copilotLoadState = CopilotLoadState.None, // Copilot sidecar state (open status, collapsed status + optional initial hand-in context)
        this.copilotSidecarState = {
            open: false,
            collapsed: false
        }, // Add goal row visibility state
        this.addGoalRowVisible = false, // Global task editor render mode preference
        this.taskEditorRenderMode = TaskEditorRenderMode.Dialog, // Currently selected task editor tab
        this.taskEditorSelectedTab = TaskEditorTab.TaskDetails, // Tracks task IDs with unseen agent output (set on assignment, cleared on Agent tab view)
        this.unseenAgentOutputTaskIds = new Set(), // View roaming data
        this.viewSettingsRoamingData = {}, this.previousViewSettingsRoamingData = null, // view settings from plan data
        // TODO: Bug 2612581: Add handling plan related actions to ViewStore once evo plan's gridSetting is available
        // private viewSettingsPlanData: IViewSettings = {};
        // Indexes
        this.indexByAccessContext = {
            [TaskAccessContext.Plan]: {},
            [TaskAccessContext.AssignedToUser]: {},
            [TaskAccessContext.MyDay]: {}
        } // maps accessContext -> contextEntityId -> viewContextId
        ;
        // Initialize left nav expanded state based on window width.
        // Do not collapse if window width is 0 (Happens when view port is set to 0 width when cached within Teams).
        this.leftNavExpandedState = window.innerWidth === 0 || window.innerWidth > LeftNavCollapseThreshold;
        this.viewWidth = window.innerWidth;
    }
}
